import api from './api';

const AdminManagementService = {
    // Get all admins
    getAdmins: async (page = 1, limit = 20, includeInactive = false) => {
        try {
            const response = await api.get(`/api/admin/management/admins`, {
                params: { page, limit, include_inactive: includeInactive }
            });
            return response.data;
        } catch (error) {
            console.error('Error fetching admins:', error);
            return {
                success: false,
                message: error.response?.data?.message || 'Failed to fetch admin accounts',
                data: { admins: [] }
            };
        }
    },

    // Create new admin
    createAdmin: async (adminData) => {
        try {
            const response = await api.post('/api/admin/management/admins', adminData);
            return response.data;
        } catch (error) {
            console.error('Error creating admin:', error);
            return {
                success: false,
                message: error.response?.data?.message || 'Failed to create admin account'
            };
        }
    },

    // Update admin role or status
    updateAdmin: async (adminId, updateData) => {
        try {
            const response = await api.put(`/api/admin/management/admins/${adminId}`, updateData);
            return response.data;
        } catch (error) {
            console.error('Error updating admin:', error);
            return {
                success: false,
                message: error.response?.data?.message || 'Failed to update admin account'
            };
        }
    },

    // Delete (deactivate) admin
    deleteAdmin: async (adminId) => {
        try {
            const response = await api.delete(`/api/admin/management/admins/${adminId}`);
            return response.data;
        } catch (error) {
            console.error('Error deleting admin:', error);
            return {
                success: false,
                message: error.response?.data?.message || 'Failed to delete admin account'
            };
        }
    },

    // Get all roles
    getAdminRoles: async () => {
        try {
            const response = await api.get('/api/admin/management/roles');
            return response.data;
        } catch (error) {
            console.error('Error fetching roles:', error);
            return {
                success: false,
                message: error.response?.data?.message || 'Failed to fetch admin roles',
                data: []
            };
        }
    },

    // Get all permissions
    getAllPermissions: async () => {
        try {
            const response = await api.get('/api/admin/management/permissions');
            return response.data;
        } catch (error) {
            console.error('Error fetching permissions:', error);
            return {
                success: false,
                message: error.response?.data?.message || 'Failed to fetch permissions',
                data: []
            };
        }
    },

    // Get permission templates
    getPermissionTemplates: async () => {
        try {
            const response = await api.get('/api/admin/management/permissions/templates');
            return response.data;
        } catch (error) {
            console.error('Error fetching permission templates:', error);
            return {
                success: false,
                message: error.response?.data?.message || 'Failed to fetch permission templates',
                data: {}
            };
        }
    },

    // ✅ UPDATED: Change admin password (Super Admin only) - Enhanced with multiple endpoint attempts
    changeAdminPassword: async (adminId, newPassword) => {
        try {
            console.log(`🔄 Attempting to change password for admin ${adminId}`);
            
            // Try multiple endpoints in sequence
            let response;
            
            // Endpoint 1: Primary reset endpoint (POST)
            try {
                console.log('🔧 Trying endpoint 1: POST /reset-password');
                response = await api.post(`/api/admin/management/admins/${adminId}/reset-password`, {
                    newPassword: newPassword
                });
                console.log('✅ Password reset successful via endpoint 1');
                return response.data;
            } catch (error1) {
                console.log('❌ Endpoint 1 failed, trying endpoint 2...');
                
                // Endpoint 2: Alternative endpoint (PUT)
                try {
                    console.log('🔧 Trying endpoint 2: PUT /password');
                    response = await api.put(`/api/admin/management/admins/${adminId}/password`, {
                        password: newPassword
                    });
                    console.log('✅ Password reset successful via endpoint 2');
                    return response.data;
                } catch (error2) {
                    console.log('❌ Endpoint 2 failed, trying endpoint 3...');
                    
                    // Endpoint 3: Fallback to general update endpoint
                    try {
                        console.log('🔧 Trying endpoint 3: PUT general update');
                        response = await api.put(`/api/admin/management/admins/${adminId}`, {
                            password: newPassword
                        });
                        console.log('✅ Password reset successful via endpoint 3');
                        return response.data;
                    } catch (error3) {
                        console.log('❌ All password change endpoints failed');
                        
                        // Provide detailed error information
                        const errors = [error1, error2, error3];
                        const statusCodes = errors.map(err => err.response?.status);
                        const messages = errors.map(err => err.response?.data?.message);
                        
                        console.error('All password change attempts failed:', {
                            statusCodes,
                            messages
                        });
                        
                        // Check if it's a 404 (endpoint not found) or other error
                        if (statusCodes.includes(404)) {
                            return {
                                success: false,
                                message: 'Password reset feature is not available. The backend endpoints for password reset are not implemented.\n\n' +
                                         'Please contact the system administrator to implement the required endpoints:\n' +
                                         '• POST /api/admin/management/admins/{id}/reset-password\n' +
                                         '• PUT /api/admin/management/admins/{id}/password'
                            };
                        }
                        
                        // Return the most specific error message
                        const specificError = errors.find(err => err.response?.data?.message) || errors[0];
                        return {
                            success: false,
                            message: specificError.response?.data?.message || 'Failed to change password. Please try again or contact support.'
                        };
                    }
                }
            }
        } catch (error) {
            console.error('Unexpected error in changeAdminPassword:', error);
            return {
                success: false,
                message: 'An unexpected error occurred while changing the password.'
            };
        }
    },

    // ✅ NEW: Regular admin change their own password (requires current password)
    changeOwnPassword: async (currentPassword, newPassword) => {
        try {
            console.log('🔄 Changing own password...');
            const response = await api.post('/api/admin/auth/change-password', {
                currentPassword: currentPassword,
                newPassword: newPassword
            });
            console.log('✅ Own password changed successfully');
            return response.data;
        } catch (error) {
            console.error('Error changing own password:', error);
            return {
                success: false,
                message: error.response?.data?.message || 'Failed to change password. Please check your current password and try again.'
            };
        }
    },

    // ✅ NEW: Enhanced password change with better error handling
    changePasswordEnhanced: async (adminId, newPassword, isOwnAccount = false, currentPassword = null) => {
        try {
            if (isOwnAccount) {
                // Changing own password - requires current password
                if (!currentPassword) {
                    return {
                        success: false,
                        message: 'Current password is required to change your own password.'
                    };
                }
                return await AdminManagementService.changeOwnPassword(currentPassword, newPassword);
            } else {
                // Super Admin changing another admin's password
                return await AdminManagementService.changeAdminPassword(adminId, newPassword);
            }
        } catch (error) {
            console.error('Error in enhanced password change:', error);
            return {
                success: false,
                message: 'Failed to process password change request.'
            };
        }
    },

    // ✅ NEW: Test password endpoints availability
    testPasswordEndpoints: async () => {
        const endpoints = [
            '/api/admin/management/admins/1/reset-password',
            '/api/admin/management/admins/1/password', 
            '/api/admin/auth/change-password'
        ];
        
        const results = {};
        
        for (const endpoint of endpoints) {
            try {
                // Test with OPTIONS request or HEAD to check if endpoint exists
                const response = await api.options(endpoint);
                results[endpoint] = {
                    exists: true,
                    methods: response.headers['allow'] || 'Unknown'
                };
            } catch (error) {
                results[endpoint] = {
                    exists: false,
                    error: error.response?.status || error.message
                };
            }
        }
        
        return results;
    }

};

export default AdminManagementService;